import React, { ReactNode, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { TypographyStyledBlack } from '../../../shared/components/tileLinkStyled/style';
import { Box, Modal, Typography } from '@mui/material';
import { BlueHeaderWithText } from '../../estimatingRequirements/peng2018/blueHeaderWithText';
import { metaDataSelector } from '../../../store/globalSelectors';
import { useAppSelector } from '../../../store/hooks';
import { NavigationButtons } from '../../../shared/components/navigationButtons';
import { ArcContainer } from '../../../shared/components/arcContainer';
import { SingleNumberCard } from '../../../shared/components/numberInput/numberCards/singleNumberCard';
import { DoubleTextBlueCard } from '../../../shared/components/doubleTextBlueCard';
import { setPaedFluidCalc } from '../../../store/slices/metaDataSlice';

import InfoIcon from '../../../assets/icons/infoIcon.svg';
import InputFluidRequirementIcon from '../../../assets/icons/inputFluidRequirementIcon.svg';
import FluidRequirementIcon from '../../../assets/icons/fluidRequirementsIcon.svg';
import { TypographyH3Bold, TypographyH4Bold } from './style';
import {
  calcMonthsOfAge,
  calculateEFSAFluidRequirement,
  calculateIOMFluidRequirement,
  calculatePediatricFluidRequirement,
  calcYearsOfAge,
  displayCalculation
} from '../../../shared/utils/utilFunctions';
import { InfoModal } from '../../../shared/components/modal/InfoModal';
import { AddOnInfoModal } from '../../../shared/components/modal/addOnInfoModal';

interface AOIModalProps {
  heading1?: string;
  heading2?: string;
  heading3?: string;
  heading4?: string;
  description1?: string;
  description2?: string;
  description3?: string;
  description4?: string;
  extraContent?: ReactNode;
}

export const PaedFluid = () => {
  const dispatch = useDispatch();
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [openAddonInfoModal, setOpenAddonInfoModal] = useState(false);
  const [modalSelected, selectModal] = useState('');
  const { patientInformation, estimatingRequirements } =
    useAppSelector(metaDataSelector);
  const { metric, age, gender } = patientInformation;
  const { years: patientAgeYears, months: patientAgeMonths } = age;
  const { weight, estimatedDryWeight } = metric;
  const { paedFluid } = estimatingRequirements;
  const calculatedAgeInYears = calcYearsOfAge(age.years, age.months);
  const calculatedAgeInMonths = calcMonthsOfAge(age.years, age.months);
  const {
    fluidRequirementsInput,
    calculatedFluidRequirements,
    holidaySegar,
    efsaFluidRequirement,
    iomRequirement
  } = paedFluid;
  const { fluidMlPerDay, fluidMlPerKgPerDay } = holidaySegar;

  const consideredWeight = estimatedDryWeight > 0 ? estimatedDryWeight : weight;

  useEffect(() => {
    let fluidRequired = 0;
    if (consideredWeight && fluidRequirementsInput) {
      fluidRequired = Number(
        (consideredWeight * fluidRequirementsInput).toFixed(2)
      );
    }

    dispatch(
      setPaedFluidCalc({
        ...paedFluid,
        calculatedFluidRequirements: fluidRequired
      })
    );
  }, [fluidRequirementsInput]);

  useEffect(() => {
    // holiday segar
    let convertedAgeInMonths = 0;
    if (!patientAgeYears && !patientAgeMonths) return;
    if (!patientAgeYears && patientAgeMonths) {
      convertedAgeInMonths = patientAgeMonths;
    } else if (patientAgeYears && !patientAgeMonths) {
      convertedAgeInMonths = patientAgeYears * 12;
    } else {
      convertedAgeInMonths = patientAgeYears * 12 + patientAgeMonths;
    }

    const { fluidMlPerDay, fluidMlPerKgPerDay } =
      calculatePediatricFluidRequirement(
        convertedAgeInMonths,
        consideredWeight
      );

    const efsaFluidRequirement = calculateEFSAFluidRequirement(
      convertedAgeInMonths,
      consideredWeight,
      gender !== '' && gender.toLowerCase()
    );

    const iomFluidRequirment = calculateIOMFluidRequirement(
      convertedAgeInMonths,
      gender !== '' && gender.toLowerCase()
    );

    dispatch(
      setPaedFluidCalc({
        ...paedFluid,
        holidaySegar: {
          ...paedFluid.holidaySegar,
          fluidMlPerDay: fluidMlPerDay,
          fluidMlPerKgPerDay: fluidMlPerKgPerDay
        },
        efsaFluidRequirement: efsaFluidRequirement,
        iomRequirement: iomFluidRequirment
      })
    );
  }, [weight, patientAgeYears, patientAgeMonths, estimatedDryWeight, gender]);

  const modalContent = (): string => {
    if (modalSelected === 'holidaysegar')
      return 'Irish Nutrition and Dietetic Institute (2022) Nutrition Support Reference Guide.';
    if (modalSelected === 'efsa')
      return 'EFSA Panel on Dietetic Products, Nutrition, and Allergies (NDA); Scientific Opinion on Dietary reference values for water. EFSA Journal 2010; 8(3):1459. [48 pp.]. doi:10.2903/j.efsa.2010.1459.';
    if (modalSelected === 'iom')
      return 'Institute of medicine, 2004. Dietary reference intakes for water.';

    return '';
  };
  const modalHeading = (): AOIModalProps => {
    if (modalSelected === 'efsa')
      return {
        heading1: 'EFSA',
        description1: ' = European Food Safety Authority'
      };
    if (modalSelected === 'iom')
      return {
        heading1: 'IOM',
        description1: ' = Institute of Medicine'
      };

    return {};
  };

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          backgroundColor: '#F2F2F2',
          p: 0,
          top: 0,
          left: 0,
          height: '100%'
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            marginTop: '1.875rem'
          }}
        >
          <TypographyStyledBlack sx={{ margin: '3.125rem 0' }}>
            FLUID REQUIREMENTS
          </TypographyStyledBlack>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <BlueHeaderWithText
              text1={
                calculatedAgeInMonths > 11 ? 'Age (years):' : 'Age (months):'
              }
              value1={
                calculatedAgeInMonths > 11
                  ? String(calculatedAgeInYears)
                  : String(calculatedAgeInMonths)
              }
              text2={'Weight (kg):'}
              value2={String(consideredWeight)}
              single={false}
              customWidth="75%"
              customMarginBottom="1rem"
              backgroundColor="#5BC2E780"
            />
          </Box>
          <ArcContainer
            icon={InputFluidRequirementIcon}
            customizedHeight="auto"
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <TypographyH4Bold className="pt-16">
                INPUT FLUID REQUIREMENTS
              </TypographyH4Bold>
              <SingleNumberCard
                width={'50%'}
                title={''}
                marginVal={'0.625rem'}
                range={{ min: 0, max: 100 }}
                subHead={'(mL/kg/day)'}
                value={fluidRequirementsInput}
                setValue={(val) => {
                  dispatch(
                    setPaedFluidCalc({
                      ...paedFluid,
                      fluidRequirementsInput: val
                    })
                  );
                }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                padding: '0.625rem 0.9375rem',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '0.625rem',
                height: '65px',
                alignSelf: 'stretch',
                backgroundColor: '#5BC2E740',
                color: '#002A3A',
                borderRadius: '0.9375rem',
                margin: '1.25rem'
              }}
            >
              <TypographyH3Bold>Fluid requirement (mL/day): </TypographyH3Bold>
              <Typography
                sx={{
                  fontFamily: 'Calibri',
                  fontWeight: 700,
                  fontSize: '1.875rem'
                }}
              >
                <span data-testid="fluidRequirement">
                  {calculatedFluidRequirements}
                </span>
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                mt: 5,
                ml: 5,
                mr: 5
              }}
            >
              <DoubleTextBlueCard
                heading1="ESTIMATED FLUID REQUIREMENTS"
                miniHeading1="Holiday-Segar Formula"
                miniHeadingSize="small"
                label1={String(fluidMlPerDay)}
                unit1={' mL/day'}
                label2={String(fluidMlPerKgPerDay)}
                unit2={' mL/kg/day'}
                icon={FluidRequirementIcon}
                mb="3.125rem"
                infoIconRequired={true}
                handleInfoIconClick={() => {
                  selectModal('holidaysegar');
                  setOpenInfoModal(true);
                }}
                infoIcon={InfoIcon}
                infoIconTestId={'fluidRequirementsInfoPopup'}
                customTopInfoIcon="-1.875rem"
              />

              <DoubleTextBlueCard
                heading1="EFSA 2010"
                label1={displayCalculation(efsaFluidRequirement)}
                unit1={' mL/day'}
                icon={FluidRequirementIcon}
                mb="3.125rem"
                infoIconRequired={true}
                handleInfoIconClick={() => {
                  selectModal('efsa');
                  setOpenAddonInfoModal(true);
                }}
                infoIcon={InfoIcon}
                infoIconTestId={'efsaInfoPopup'}
                customTopInfoIcon="-1.875rem"
              />

              <DoubleTextBlueCard
                heading1="IOM 2005"
                label1={String(iomRequirement)}
                unit1={' mL/day'}
                icon={FluidRequirementIcon}
                infoIconRequired={true}
                handleInfoIconClick={() => {
                  selectModal('iom');
                  setOpenAddonInfoModal(true);
                }}
                infoIcon={InfoIcon}
                infoIconTestId={'iomInfoPopup'}
                customTopInfoIcon="-1.875rem"
              />
            </Box>
          </ArcContainer>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: 2 }}>
          <NavigationButtons nextRoute={'/summary'} nextDisabled={false} />
        </Box>
      </Box>
      <InfoModal
        open={openInfoModal}
        onClose={() => {
          setOpenInfoModal(false);
          selectModal('');
        }}
        title={'Reference: '}
        content={modalContent()}
      />
      <Modal
        open={openAddonInfoModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <AddOnInfoModal
          heading1={modalHeading()?.heading1}
          description1={modalHeading()?.description1}
          title={`Reference:`}
          content={modalContent()}
          onClose={() => {
            setOpenAddonInfoModal(false);
            selectModal('');
          }}
        />
      </Modal>
    </>
  );
};
